import { useState } from 'react';
import { Button, Field, Input, Panel, Select, StatusBadge } from '../../components';
import type { CitPositionRequest, CitPositionResult } from '../../lib/api';
import { formatKobo } from './invoice';

export interface BusinessCitFactsInput {
  taxId: string;
  grossTurnoverNaira: string;
  totalFixedAssetsNaira: string;
  aggregateGroupTurnoverNaira: string;
  accountingProfitBeforeTaxNaira: string;
  additionsNaira: string;
  deductionsNaira: string;
  broughtForwardLossNaira: string;
  otherAssessableProfitNaira: string;
  capitalAllowanceBroughtForwardNaira: string;
  currentCapitalAllowanceClass: 'NONE' | 'CLASS_1' | 'CLASS_2' | 'CLASS_3';
  currentCapitalAllowanceBaseNaira: string;
  transitionReviewReference: string;
}

export function BusinessCitReturnInteraction({ businessName, busy = false, calculation, onCalculate }: {
  businessName: string; busy?: boolean; calculation?: CitPositionResult;
  onCalculate(values: BusinessCitFactsInput): Promise<void>;
}) {
  const [values, setValues] = useState<BusinessCitFactsInput>({ taxId: '', grossTurnoverNaira: '',
    totalFixedAssetsNaira: '', aggregateGroupTurnoverNaira: '', accountingProfitBeforeTaxNaira: '',
    additionsNaira: '0', deductionsNaira: '0', broughtForwardLossNaira: '0', otherAssessableProfitNaira: '0',
    capitalAllowanceBroughtForwardNaira: '0', currentCapitalAllowanceClass: 'NONE',
    currentCapitalAllowanceBaseNaira: '0', transitionReviewReference: 'Reviewed for the 2026 candidate CIT position' });
  const [error, setError] = useState<string>();
  const [dirty, setDirty] = useState(false);
  const update = <K extends keyof BusinessCitFactsInput>(key: K, value: BusinessCitFactsInput[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  return <Panel id="return-facts" tabIndex={-1} title="Calculate company income tax" description="Supported test path: an ordinary Nigerian resident company, 2026 year of assessment, with a 1 January–31 December 2025 basis period. Enter only figures you have reviewed.">
    <form data-amoye-unsaved={dirty ? 'true' : 'false'} onChange={() => setDirty(true)}
      onSubmit={async (event) => { event.preventDefault(); setError(undefined);
      try { await onCalculate(values); setDirty(false); } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'The company income tax position could not be calculated.');
      } }}>
      <div className="am-b1-form-grid">
        <Field label="Company" hint="This calculation is bound to the signed-in business."><Input disabled value={businessName} /></Field>
        <Field label="Tax identification number" required><Input required value={values.taxId}
          onChange={(event) => update('taxId', event.target.value)} /></Field>
        <MoneyField label="Gross turnover (naira)" value={values.grossTurnoverNaira} onChange={(value) => update('grossTurnoverNaira', value)} />
        <MoneyField label="Total fixed assets (naira)" value={values.totalFixedAssetsNaira} onChange={(value) => update('totalFixedAssetsNaira', value)} />
        <MoneyField label="Aggregate group turnover (naira)" value={values.aggregateGroupTurnoverNaira} onChange={(value) => update('aggregateGroupTurnoverNaira', value)} />
        <MoneyField allowNegative label="Accounting profit or loss before tax (naira)" value={values.accountingProfitBeforeTaxNaira}
          onChange={(value) => update('accountingProfitBeforeTaxNaira', value)} />
        <MoneyField label="Tax additions (naira)" value={values.additionsNaira} onChange={(value) => update('additionsNaira', value)} />
        <MoneyField label="Tax deductions (naira)" value={values.deductionsNaira} onChange={(value) => update('deductionsNaira', value)} />
        <MoneyField label="Same-trade losses brought forward (naira)" value={values.broughtForwardLossNaira}
          onChange={(value) => update('broughtForwardLossNaira', value)} />
        <MoneyField label="Other assessable profit (naira)" value={values.otherAssessableProfitNaira}
          onChange={(value) => update('otherAssessableProfitNaira', value)} />
        <MoneyField label="Capital allowance brought forward (naira)" value={values.capitalAllowanceBroughtForwardNaira}
          onChange={(value) => update('capitalAllowanceBroughtForwardNaira', value)} />
        <Field label="Current capital allowance class" hint="Choose none unless the asset schedule and eligibility were reviewed.">
          <Select value={values.currentCapitalAllowanceClass} onChange={(event) => update('currentCapitalAllowanceClass',
            event.target.value as BusinessCitFactsInput['currentCapitalAllowanceClass'])}>
            <option value="NONE">None</option><option value="CLASS_1">Class 1</option>
            <option value="CLASS_2">Class 2</option><option value="CLASS_3">Class 3</option>
          </Select></Field>
        <MoneyField disabled={values.currentCapitalAllowanceClass === 'NONE'} label="Current capital allowance base (naira)"
          value={values.currentCapitalAllowanceBaseNaira} onChange={(value) => update('currentCapitalAllowanceBaseNaira', value)} />
        <Field label="Review reference" hint="A note or internal reference identifying who reviewed the transition treatment." required>
          <Input required value={values.transitionReviewReference}
            onChange={(event) => update('transitionReviewReference', event.target.value)} /></Field>
      </div>
      <label className="am-b1-review-confirmation"><input required type="checkbox" />
        <span>I reviewed these company, trade, loss and capital-allowance figures. OCR and AI suggestions are not being treated as authoritative tax facts.</span></label>
      {error && <p className="am-b1-invoice-error" role="alert">{error}</p>}
      <Button loading={busy} type="submit">Calculate CIT estimate</Button>
    </form>
    {calculation && <section aria-label="CIT calculation estimate" className="am-b1-artifact">
      <div><span>Company classification</span><strong>{calculation.companyClassification === 'SMALL_COMPANY' ? 'Small company' : 'Standard company'}</strong></div>
      <div><span>Company income tax</span><strong>{formatKobo(calculation.citKobo)}</strong></div>
      <div><span>Development levy</span><strong>{formatKobo(calculation.developmentLevyKobo)}</strong></div>
      <div><span>Gross estimated amount</span><strong>{formatKobo(calculation.grossStatutoryAmountKobo)}</strong></div>
      <div><span>Calculation status</span><StatusBadge tone="warning">Candidate · professional review required</StatusBadge></div>
      <details><summary>View calculation audit details</summary>
        <div><span>Rules</span><code>{calculation.packageId} · {calculation.packageVersion}</code></div>
        <div><span>Calculation reference</span><code>{calculation.outputSha256}</code></div>
        <ul>{calculation.trace.map((line) => <li key={line.id}>{line.operation.replaceAll('_', ' ').toLowerCase()} — {formatKobo(line.resultKobo)}</li>)}</ul>
      </details>
      <p>This is a deterministic candidate estimate. Return preparation, filing and payment remain unavailable until the professional decisions shown in the test plan are accepted and NRS publishes the CIT filing contract.</p>
    </section>}
  </Panel>;
}

export async function bindBusinessCitFacts(values: BusinessCitFactsInput, identity: {
  taxpayerId: string; legalName: string; engagementId: string;
}): Promise<CitPositionRequest> {
  if (!values.taxId.trim()) throw new Error('Enter the company tax identification number.');
  if (!values.transitionReviewReference.trim()) throw new Error('Enter a review reference.');
  if (values.currentCapitalAllowanceClass === 'NONE' && values.currentCapitalAllowanceBaseNaira !== '0') {
    throw new Error('Choose a capital allowance class or set the current allowance base to zero.');
  }
  const facts: CitPositionRequest['facts'] = { kind: 'CIT', taxId: values.taxId.trim(), legalName: identity.legalName,
    grossTurnoverKobo: nairaToKobo(values.grossTurnoverNaira), totalFixedAssetsKobo: nairaToKobo(values.totalFixedAssetsNaira),
    aggregateGroupTurnoverKobo: nairaToKobo(values.aggregateGroupTurnoverNaira),
    accountingProfitBeforeTaxKobo: nairaToKobo(values.accountingProfitBeforeTaxNaira, true),
    additionsKobo: nairaToKobo(values.additionsNaira), deductionsKobo: nairaToKobo(values.deductionsNaira),
    broughtForwardLossKobo: nairaToKobo(values.broughtForwardLossNaira),
    otherAssessableProfitKobo: nairaToKobo(values.otherAssessableProfitNaira),
    capitalAllowanceBroughtForwardKobo: nairaToKobo(values.capitalAllowanceBroughtForwardNaira),
    currentCapitalAllowanceClass: values.currentCapitalAllowanceClass,
    currentCapitalAllowanceBaseKobo: nairaToKobo(values.currentCapitalAllowanceBaseNaira),
    transitionReviewReference: values.transitionReviewReference.trim(), humanReviewed: true };
  const packageId = 'AMOYE-CIT-NRS-ANNUAL-ORDINARY-CAND-001' as const;
  const reviewedFactSnapshotSha256 = await sha256(stableJson({ schemaVersion: 'amoye-reviewed-business-cit-facts-1',
    taxpayerId: identity.taxpayerId, engagementId: identity.engagementId, packageId, facts }));
  return { packageId, reviewedFactSnapshotSha256, facts };
}

function MoneyField({ allowNegative = false, disabled = false, label, value, onChange }: { allowNegative?: boolean;
  disabled?: boolean; label: string; value: string; onChange(value: string): void }) {
  return <Field label={label} hint="No commas; at most two decimal places." required><Input disabled={disabled} inputMode="decimal"
    pattern={allowNegative ? "-?(0|[1-9][0-9]*)([.][0-9]{1,2})?" : "(0|[1-9][0-9]*)([.][0-9]{1,2})?"}
    required value={value} onChange={(event) => onChange(event.target.value)} /></Field>;
}

function nairaToKobo(value: string, signed = false): string {
  const pattern = signed ? /^-?(0|[1-9]\d*)(\.\d{1,2})?$/ : /^(0|[1-9]\d*)(\.\d{1,2})?$/;
  if (!pattern.test(value)) throw new Error('Enter each amount as naira with no commas and no more than two decimal places.');
  const negative = value.startsWith('-'); const normalized = negative ? value.slice(1) : value;
  const [whole = '0', fraction = ''] = normalized.split('.');
  const kobo = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return `${negative ? '-' : ''}${kobo}`;
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`;
  return JSON.stringify(value);
}
